import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  Loader2,
  Mail,
  Plus,
  RefreshCw,
  Inbox as InboxIcon,
  Archive,
  Paperclip,
  AlertCircle,
  CheckCircle,
} from 'lucide-react'
import { api, ApiError } from '../lib/api'
import NewAccountModal from '../components/email/NewAccountModal'
import MessageReader from '../components/email/MessageReader'
import UnassignedPanel from '../components/inbox/UnassignedPanel'
import {
  EmailAccountStatusLabel,
  type EmailAccountDto,
  type EmailListItem,
  type EmailListResponse,
} from '@toga/shared'

type Filter = 'all' | 'unread' | 'archived' | 'unarchived'

const PAGE_SIZE = 50

function MailView() {
  const [accounts, setAccounts] = useState<EmailAccountDto[] | null>(null)
  const [accountFilter, setAccountFilter] = useState<string>('') // '' = todas
  const [filter, setFilter] = useState<Filter>('all')
  const [q, setQ] = useState('')
  const [data, setData] = useState<EmailListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showNewAccount, setShowNewAccount] = useState(false)
  const [syncBusy, setSyncBusy] = useState<string | null>(null)

  const loadAccounts = useCallback(async () => {
    try {
      const { items } = await api.get<{ items: EmailAccountDto[] }>('/email-accounts')
      setAccounts(items)
    } catch (err) {
      if (err instanceof ApiError) {
        // 401 etc., dejamos accounts vacío
      }
      setAccounts([])
    }
  }, [])

  const loadMessages = useCallback(async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      if (q.trim()) params.set('q', q.trim())
      if (accountFilter) params.set('accountId', accountFilter)
      if (filter === 'unread') params.set('isRead', 'false')
      if (filter === 'archived') params.set('archived', 'true')
      if (filter === 'unarchived') params.set('archived', 'false')
      params.set('pageSize', String(PAGE_SIZE))
      const resp = await api.get<EmailListResponse>(`/emails?${params.toString()}`)
      setData(resp)
    } catch {
      setData({ items: [], total: 0, page: 1, pageSize: PAGE_SIZE })
    } finally {
      setLoading(false)
    }
  }, [q, accountFilter, filter])

  useEffect(() => {
    void loadAccounts()
  }, [loadAccounts])

  useEffect(() => {
    void loadMessages()
  }, [loadMessages])

  const onSync = async (accountId: string) => {
    setSyncBusy(accountId)
    try {
      await api.post(`/email-accounts/${accountId}/sync`)
      // Damos un par de segundos al worker.
      setTimeout(() => {
        void loadAccounts()
        void loadMessages()
      }, 3500)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Error')
    } finally {
      setTimeout(() => setSyncBusy(null), 3500)
    }
  }

  // Si no hay cuentas, pantalla de onboarding.
  if (accounts !== null && accounts.length === 0) {
    return (
      <div className="max-w-xl mx-auto pt-12 surface p-10 text-center animate-slideUp">
        <div className="mx-auto w-16 h-16 rounded-2xl grid place-items-center bg-toga-500/15 text-toga-300 shadow-glow-toga mb-4">
          <Mail size={28} />
        </div>
        <h2 className="text-2xl font-serif font-semibold t-primary mb-2">
          Conecta tu primer correo
        </h2>
        <p className="t-muted text-sm mb-5">
          Sincroniza tu bandeja de Gmail, Outlook, iCloud u otro IMAP. Los correos quedarán
          disponibles aquí y los podrás archivar manualmente en clientes o expedientes.
        </p>
        <button
          type="button"
          onClick={() => setShowNewAccount(true)}
          className="px-4 py-2 rounded-lg text-sm font-semibold text-white bg-gradient-to-r from-toga-600 via-toga-500 to-toga-700 hover:shadow-glow-toga inline-flex items-center gap-2"
        >
          <Plus size={16} /> Conectar cuenta
        </button>
        {showNewAccount && (
          <NewAccountModal
            onClose={() => setShowNewAccount(false)}
            onCreated={() => {
              setShowNewAccount(false)
              void loadAccounts()
              void loadMessages()
            }}
          />
        )}
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[220px_360px_1fr] gap-4 h-[calc(100vh-12.5rem)] animate-slideUp">
      {/* Sidebar de cuentas y filtros */}
      <aside className="surface p-3 overflow-y-auto">
        <div className="flex items-center justify-between mb-2 px-1">
          <h3 className="text-xs uppercase tracking-wider t-faint">Cuentas</h3>
          <button
            type="button"
            onClick={() => setShowNewAccount(true)}
            className="w-7 h-7 grid place-items-center rounded-lg hover:bg-hover t-muted hover:text-toga-300"
            title="Conectar cuenta"
          >
            <Plus size={14} />
          </button>
        </div>
        <button
          type="button"
          onClick={() => setAccountFilter('')}
          className={[
            'w-full text-left px-2.5 py-1.5 rounded-lg text-sm flex items-center gap-2 transition mb-2',
            !accountFilter ? 'bg-toga-500/15 text-toga-200' : 't-secondary hover:bg-hover',
          ].join(' ')}
        >
          <InboxIcon size={14} /> Todas
        </button>
        {accounts?.map((a) => (
          <div
            key={a.id}
            className={[
              'rounded-lg px-2.5 py-1.5 mb-1',
              accountFilter === a.id ? 'bg-toga-500/15 text-toga-200' : 't-secondary',
            ].join(' ')}
          >
            <div className="flex items-center justify-between gap-1">
              <button
                type="button"
                onClick={() => setAccountFilter(a.id)}
                className="text-sm text-left truncate flex-1 min-w-0"
                title={a.emailAddress}
              >
                <div className="truncate">{a.displayName || a.emailAddress}</div>
                <div className="text-[11px] t-faint truncate">{a.emailAddress}</div>
              </button>
              <button
                type="button"
                onClick={() => onSync(a.id)}
                disabled={syncBusy === a.id}
                className="w-6 h-6 grid place-items-center rounded hover:bg-hover t-faint hover:text-toga-300 disabled:opacity-50"
                title="Sincronizar"
              >
                {syncBusy === a.id ? (
                  <Loader2 size={12} className="animate-spin" />
                ) : (
                  <RefreshCw size={12} />
                )}
              </button>
            </div>
            {a.status !== 'ACTIVA' && (
              <div className="text-[11px] mt-0.5 text-amber-300 flex items-center gap-1">
                <AlertCircle size={10} /> {EmailAccountStatusLabel[a.status]}
              </div>
            )}
            {a.lastSyncAt && (
              <div className="text-[10px] t-faint mt-0.5">
                Última sync: {new Date(a.lastSyncAt).toLocaleString('es-ES')}
              </div>
            )}
          </div>
        ))}

        <div className="mt-4">
          <h3 className="text-xs uppercase tracking-wider t-faint mb-2 px-1">Filtros</h3>
          {(
            [
              { id: 'all' as Filter, label: 'Todos', icon: Mail },
              { id: 'unread' as Filter, label: 'Sin leer', icon: AlertCircle },
              { id: 'archived' as Filter, label: 'Archivados', icon: Archive },
              { id: 'unarchived' as Filter, label: 'Sin archivar', icon: CheckCircle },
            ] satisfies Array<{ id: Filter; label: string; icon: typeof Mail }>
          ).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setFilter(id)}
              className={[
                'w-full text-left px-2.5 py-1.5 rounded-lg text-sm flex items-center gap-2 transition',
                filter === id ? 'bg-toga-500/15 text-toga-200' : 't-secondary hover:bg-hover',
              ].join(' ')}
            >
              <Icon size={14} /> {label}
            </button>
          ))}
        </div>
      </aside>

      {/* Lista de mensajes */}
      <section className="surface overflow-y-auto">
        <div className="sticky top-0 backdrop-blur bg-topbar px-3 py-2.5 border-b border-base z-10">
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar en tus correos…"
            className="w-full px-3 py-1.5 rounded-full bg-slate-900/60 border border-slate-700/60 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-toga-500/40"
          />
        </div>
        {loading && !data ? (
          <div className="p-8 t-muted flex items-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Cargando…
          </div>
        ) : data && data.items.length === 0 ? (
          <p className="p-8 t-muted text-sm text-center">
            {q ? 'Sin resultados.' : 'La bandeja está vacía.'}
          </p>
        ) : (
          <ul>
            {data?.items.map((m) => (
              <MessageRow
                key={m.id}
                m={m}
                selected={selectedId === m.id}
                onClick={() => setSelectedId(m.id)}
              />
            ))}
          </ul>
        )}
      </section>

      {/* Panel de lectura */}
      <section className="surface overflow-hidden">
        <MessageReader
          emailId={selectedId}
          onArchived={() => {
            void loadMessages()
          }}
        />
      </section>

      {showNewAccount && (
        <NewAccountModal
          onClose={() => setShowNewAccount(false)}
          onCreated={() => {
            setShowNewAccount(false)
            void loadAccounts()
            void loadMessages()
          }}
        />
      )}
    </div>
  )
}

function MessageRow({
  m,
  selected,
  onClick,
}: {
  m: EmailListItem
  selected: boolean
  onClick: () => void
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className={[
          'w-full text-left px-3 py-3 border-b border-base/50 transition',
          selected ? 'bg-toga-500/10' : 'hover:bg-hover/50',
        ].join(' ')}
      >
        <div className="flex items-center justify-between gap-2">
          <span
            className={[
              'text-sm truncate',
              m.isRead ? 't-secondary' : 't-primary font-semibold',
            ].join(' ')}
          >
            {m.fromAddress.replace(/<.+>/, '').trim() || m.fromEmail}
          </span>
          <span className="text-[10px] t-faint shrink-0">
            {new Date(m.receivedAt).toLocaleDateString('es-ES', {
              day: '2-digit',
              month: '2-digit',
            })}
          </span>
        </div>
        <div
          className={['text-sm truncate mt-0.5', m.isRead ? 't-secondary' : 't-primary'].join(' ')}
        >
          {m.subject || '(sin asunto)'}
        </div>
        <div className="text-xs t-faint truncate mt-0.5">{m.snippet}</div>
        <div className="flex items-center gap-2 mt-1">
          {m.hasAttachments && <Paperclip size={11} className="t-faint" />}
          {(m.archivedToCaseCode || m.archivedToClientName) && (
            <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300 border border-emerald-500/20">
              <Archive size={9} className="inline mr-0.5" />
              {m.archivedToCaseCode ?? m.archivedToClientName}
            </span>
          )}
        </div>
      </button>
    </li>
  )
}

/**
 * Buzón: el correo de las cuentas conectadas y, al lado, la bandeja «Sin asignar» (A3) con
 * lo que ha entrado por cualquier canal y aún no cuelga de un cliente.
 */
export default function Inbox() {
  const [params, setParams] = useSearchParams()
  const view = params.get('tab') === 'sin-asignar' ? 'unassigned' : 'mail'
  const [pending, setPending] = useState<number | null>(null)

  // El número de pendientes se conoce aunque se esté viendo el correo.
  useEffect(() => {
    api
      .get<{ pendingCount: number }>('/communications/unassigned?pageSize=1')
      .then((r) => setPending(r.pendingCount))
      .catch(() => setPending(null))
  }, [view])

  const tabCls = (active: boolean) =>
    [
      'px-3.5 py-1.5 rounded-lg text-sm border',
      active
        ? 'border-toga-500/50 bg-toga-500/10 t-primary'
        : 'border-base t-secondary hover:bg-hover',
    ].join(' ')

  return (
    <div className="space-y-4">
      <div className="flex gap-2">
        <button type="button" className={tabCls(view === 'mail')} onClick={() => setParams({})}>
          Correo
        </button>
        <button
          type="button"
          className={tabCls(view === 'unassigned')}
          onClick={() => setParams({ tab: 'sin-asignar' })}
        >
          Sin asignar
          {pending != null && pending > 0 && (
            <span className="ml-1.5 text-[11px] px-1.5 py-0.5 rounded-full bg-toga-500/20 text-toga-200">
              {pending}
            </span>
          )}
        </button>
      </div>
      {view === 'mail' ? <MailView /> : <UnassignedPanel onCount={setPending} />}
    </div>
  )
}
